import React, { useEffect, useState } from 'react';
import {
  fetchContactDetails,
  fetchCallHistory,
  fetchContactUpdates,
  fetchSubitems,
  createSubitem,
  getLinkedContactId,
  updateContactDetails,
  updateProspectDetails,
  discardProspect,
  reassignProspect,
  mondayItemUrl,
  PROSPECTING_BOARD_ID,
  CONTACTS_BOARD_ID,
} from '../api/monday';
import { BROKERS } from '../config/brokers';

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
  indigo:       '#534AB7',
  indigoDark:   '#3C3489',
  indigoLight:  '#EEEDFE',
  teal:         '#1D9E75',
  tealLight:    '#E1F5EE',
  tealDark:     '#085041',
  amber:        '#633806',
  amberLight:   '#FAEEDA',
  red:          '#DC2626',
  redLight:     '#FEF2F2',
  redBorder:    '#FECACA',
  bgPage:       '#f5f4f1',
  bgCard:       '#ffffff',
  bgSoft:       '#f0efe9',
  border:       '#e8e6e0',
  borderSoft:   '#f3f2ef',
  textPrimary:  '#111827',
  textSecondary:'#6b7280',
  textTertiary: '#9ca3af',
};

// ── Constants ──────────────────────────────────────────────────────────────
const PRIORITY_OPTIONS     = ['(1) Urgent', '(2) Motivated', '(3) Active'];
const PROSPECT_TYPE_OPTIONS = ['Int Requirement', 'Ext Requirement', 'Off Market / Pocket'];

const DISCARD_REASONS = ['Broker', 'Has representation', 'Not a fit', 'Hostile—DNC', 'Wrong number'];
const DISCARD_DNC     = ['Hostile—DNC'];

// ── Requirements / Subitems constants ─────────────────────────────────────
// These must match the option labels on the Monday subitems board exactly.
const REQUIREMENT_STATUSES = [
  'Looking', 'Identified', 'Touring', 'LOI/Contracting',
  'Under Contract', 'Closed', 'On Hold', 'Dead',
  'Looking-Lease', 'Identified-Lease', 'Touring-Lease', 'Closed-Lease',
];

// dropdown column — update labels to match Monday board options
const ASSIGNMENT_TYPES = ['Buyer Rep', 'Tenant Rep', 'Seller Rep', 'Landlord Rep'];

// dropdown2 column — update labels to match Monday board options
const ASSET_TYPES_LIST = [
  'Industrial', 'Office', 'Retail', 'Multi-Family',
  'Land', 'Flex/Warehouse', 'Mixed-Use', 'NNN',
];

// dropdown6 column — update labels to match Monday board options
const LOCATIONS = [
  'Missoula', 'Bozeman', 'Helena', 'Billings',
  'Great Falls', 'Kalispell', 'Western MT', 'Eastern MT', 'Statewide',
];

const REQUIREMENT_STATUS_COLORS = {
  'looking':           { bg: '#EEEDFE',     color: '#3C3489' },
  'identified':        { bg: '#FFF7ED',     color: '#C2410C' },
  'touring':           { bg: '#FFFBEB',     color: '#B45309' },
  'loi/contracting':   { bg: '#F3E8FF',     color: '#6D28D9' },
  'under contract':    { bg: '#E1F5EE',     color: '#085041' },
  'closed':            { bg: '#DCFCE7',     color: '#14532D' },
  'on hold':           { bg: '#F3F4F6',     color: '#4B5563' },
  'dead':              { bg: '#FEF2F2',     color: '#991B1B' },
  'looking-lease':     { bg: '#EEEDFE',     color: '#3C3489' },
  'identified-lease':  { bg: '#FFF7ED',     color: '#C2410C' },
  'touring-lease':     { bg: '#FFFBEB',     color: '#B45309' },
  'closed-lease':      { bg: '#DCFCE7',     color: '#14532D' },
};

function reqStatusColor(status) {
  return REQUIREMENT_STATUS_COLORS[(status || '').toLowerCase()]
    || { bg: '#F3F4F6', color: '#4B5563' };
}

// Strips any currency prefix/suffix Monday might return (e.g. "$5.50" → 5.5)
function parseNum(v) {
  if (!v && v !== 0) return NaN;
  const cleaned = String(v).replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned);
}

// Abbreviated formatter for SF and price (1500 → "1.5K", 2000000 → "2M")
function fmtNum(v) {
  const n = parseNum(v);
  if (isNaN(n)) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

// Plain decimal formatter for acres — never abbreviates with K/M
function fmtAcres(v) {
  const n = parseNum(v);
  if (isNaN(n)) return '';
  // Show up to 2 decimal places, strip trailing zeros
  return parseFloat(n.toFixed(2)).toString();
}

function rangeStr(min, max, unit = '', money = false) {
  const fmt = (v) => { const s = fmtNum(v); return s ? (money ? `$${s}` : unit ? `${s} ${unit}` : s) : ''; };
  const a = fmt(min), b = fmt(max);
  if (a && b) return `${a} – ${b}`;
  if (a) return `${a}+`;
  if (b) return `Up to ${b}`;
  return '';
}

// Acres-specific range string — uses full decimal, not K/M
function acresRangeStr(min, max) {
  const fmt = (v) => { const s = fmtAcres(v); return s ? `${s} ac` : ''; };
  const a = fmt(min), b = fmt(max);
  if (a && b) return `${a} – ${b}`;
  if (a) return `${a}+`;
  if (b) return `Up to ${b}`;
  return '';
}

function autoReqName(form) {
  const parts = [];
  if (form.assetTypes?.length) parts.push(form.assetTypes.join('/'));
  if (form.assignmentType)     parts.push(form.assignmentType);
  return parts.length > 0 ? parts.join(' — ') : 'New Requirement';
}

const DEFAULT_REQ_FORM = {
  status: 'Looking', assignmentType: '', assetTypes: [],
  sfMin: '', sfMax: '', acresMin: '', acresMax: '',
  priceMin: '', priceMax: '', location: '', notes: '',
};

// ── Helpers ────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
  { bg: C.indigoLight, color: C.indigoDark },
  { bg: C.tealLight,   color: C.tealDark   },
  { bg: C.amberLight,  color: C.amber      },
  { bg: '#EAF3DE',     color: '#27500A'    },
  { bg: C.redLight,    color: '#791F1F'    },
  { bg: '#F3F0FF',     color: '#5B21B6'    },
];

function getAvatarStyle(name) {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function getInitials(name) {
  const parts = (name || '').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return '?';
}

function formatDate(dateStr) {
  if (!dateStr || dateStr === '—') return '—';
  const [y, m, d] = dateStr.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function priorityLabel(text) { return text?.replace(/^\(\d\)\s*/, '') || '—'; }

function priorityTag(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('urgent'))    return { bg: C.redLight,    color: '#991B1B' };
  if (t.includes('motivated')) return { bg: C.amberLight,  color: C.amber   };
  if (t.includes('active'))    return { bg: '#EAF3DE',     color: '#27500A' };
  return { bg: C.bgSoft, color: C.textSecondary };
}

function outcomeColor(outcome) {
  const o = (outcome || '').toLowerCase();
  if (o.includes('reached')) return C.teal;
  if (o.includes('email'))   return C.indigo;
  return C.textTertiary;
}

function getCol(item, colId) {
  return item.column_values?.find((c) => c.id === colId)?.text || '';
}

// ── Styles ─────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px', margin: '0 auto',
    minHeight: '100vh', backgroundColor: C.bgPage,
    display: 'flex', flexDirection: 'column',
  },
  nav: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', backgroundColor: C.bgCard,
    borderBottom: `1px solid ${C.border}`,
    position: 'sticky', top: 0, zIndex: 10,
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '20px', color: C.indigo, padding: '0 4px', lineHeight: 1,
    fontWeight: '600',
  },
  navTitle: { fontSize: '15px', fontWeight: '600', color: C.textPrimary, margin: 0, flex: 1 },
  navSub: { fontSize: '12px', color: C.textTertiary },

  body: { flex: 1, padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' },

  card: {
    backgroundColor: C.bgCard, borderRadius: '14px',
    padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  cardLabel: {
    fontSize: '10px', fontWeight: '700', color: C.textTertiary,
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px',
  },

  // Contact header
  contactHeader: { display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' },
  avatar: {
    width: '52px', height: '52px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '17px', fontWeight: '700', flexShrink: 0,
  },
  contactName: { fontSize: '18px', fontWeight: '700', color: C.textPrimary, margin: '0 0 2px' },
  contactCompany: { fontSize: '13px', color: C.textSecondary, margin: 0 },
  editSmallBtn: {
    marginLeft: 'auto', background: 'none',
    border: `1px solid ${C.border}`, borderRadius: '6px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '600',
    color: C.textSecondary, cursor: 'pointer',
  },

  // Info rows
  infoRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '7px 0', borderBottom: `1px solid ${C.borderSoft}`,
  },
  infoLabel: { fontSize: '12px', color: C.textSecondary },
  infoValue: { fontSize: '13px', color: C.textPrimary, fontWeight: '500' },
  infoLink: { fontSize: '13px', fontWeight: '600', color: C.indigo, textDecoration: 'none' },
  mondayLink: {
    fontSize: '11px', fontWeight: '600', color: C.textTertiary,
    textDecoration: 'none', letterSpacing: '0.02em',
  },

  // Tags
  tag: {
    display: 'inline-block', fontSize: '11px', fontWeight: '600',
    padding: '3px 9px', borderRadius: '10px',
  },

  // Edit form
  inputRow: { display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '10px' },
  inputLabel: { fontSize: '11px', fontWeight: '700', color: C.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: {
    border: `1.5px solid ${C.border}`, borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', color: C.textPrimary,
    outline: 'none', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  select: {
    border: `1.5px solid ${C.border}`, borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', color: C.textPrimary,
    outline: 'none', width: '100%', boxSizing: 'border-box',
    backgroundColor: C.bgCard, fontFamily: 'inherit',
  },
  editActions: { display: 'flex', gap: '8px', marginTop: '12px' },
  saveEditBtn: {
    flex: 1, padding: '9px', borderRadius: '8px', border: 'none',
    backgroundColor: C.indigo, color: '#fff',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  cancelEditBtn: {
    flex: 1, padding: '9px', borderRadius: '8px',
    border: `1px solid ${C.border}`, backgroundColor: C.bgCard,
    color: C.textPrimary, fontSize: '14px', cursor: 'pointer',
  },
  saveStatus: { fontSize: '13px', padding: '4px 0' },

  // Last note
  noteText: {
    fontSize: '13px', color: C.textSecondary,
    fontStyle: 'italic', lineHeight: '1.5',
  },

  // Prospect details grid
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  detailLabel: { fontSize: '11px', fontWeight: '700', color: C.textTertiary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' },
  detailValue: { fontSize: '13px', color: C.textPrimary, fontWeight: '500' },

  // Call history
  historyEntry: { padding: '11px 0', borderBottom: `1px solid ${C.borderSoft}` },
  historyMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' },
  historyDate: { fontSize: '11px', color: C.textTertiary },
  historyType: { fontSize: '12px', fontWeight: '600', color: C.textSecondary },
  outcomeDot: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  historyOutcome: { fontSize: '12px', fontWeight: '600' },
  historyNotes: { fontSize: '13px', color: C.textSecondary, lineHeight: '1.45', margin: 0 },
  noHistory: { fontSize: '13px', color: C.textTertiary, fontStyle: 'italic' },
  updateBadge: {
    fontSize: '10px', fontWeight: '700', padding: '1px 6px',
    borderRadius: '4px', backgroundColor: C.indigoLight, color: C.indigoDark,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  },

  // Collapsible section header
  sectionHeaderRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', userSelect: 'none',
  },
  sectionHeaderLabel: {
    fontSize: '10px', fontWeight: '700', color: C.textTertiary,
    textTransform: 'uppercase', letterSpacing: '0.07em',
  },
  sectionHeaderRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  chevron: {
    fontSize: '14px', color: C.textTertiary,
    lineHeight: 1, transition: 'transform 0.15s',
  },

  // Action buttons
  btnPrimary: {
    width: '100%', padding: '14px',
    backgroundColor: C.teal, color: '#fff',
    border: 'none', borderRadius: '12px',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
  },
  btnSecondary: {
    width: '100%', padding: '12px', backgroundColor: C.bgCard,
    color: C.textPrimary, border: `1px solid ${C.border}`,
    borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
  },
  btnDanger: {
    width: '100%', padding: '12px', backgroundColor: C.bgCard,
    color: C.red, border: `1px solid ${C.redBorder}`,
    borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
  },

  loading:  { textAlign: 'center', padding: '40px 20px', color: C.textTertiary },
  error:    { background: C.redLight, borderRadius: '10px', padding: '14px', color: '#991B1B', fontSize: '13px' },
  warning:  { background: C.amberLight, border: `1px solid #FCD34D`, borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#92400E' },
};

// ── CollapsibleCard ────────────────────────────────────────────────────────
function CollapsibleCard({ label, defaultOpen = true, right = null, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={s.card}>
      <div style={{ ...s.sectionHeaderRow, paddingBottom: open ? '12px' : '0' }} onClick={() => setOpen((v) => !v)}>
        <span style={s.sectionHeaderLabel}>{label}</span>
        <div style={s.sectionHeaderRight} onClick={(e) => e.stopPropagation()}>
          {right}
          <span
            style={{ ...s.chevron, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          >
            ▾
          </span>
        </div>
      </div>
      {open && children}
    </div>
  );
}

// ── Contact Info Section ────────────────────────────────────────────────────
function ContactInfoSection({ contact, contactId, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  function startEdit() {
    setForm({ name: contact.name, phone: contact.phone, email: contact.email, company: contact.company });
    setEditing(true); setStatus(null);
  }
  function cancelEdit() { setEditing(false); setForm({}); setStatus(null); }

  async function saveEdit() {
    setSaving(true); setStatus(null);
    try {
      const changes = {};
      if (form.name    !== contact.name)    changes.name    = form.name;
      if (form.phone   !== contact.phone)   changes.phone   = form.phone;
      if (form.email   !== contact.email)   changes.email   = form.email;
      if (form.company !== contact.company) changes.company = form.company;
      if (Object.keys(changes).length > 0) {
        await updateContactDetails(contactId, changes);
        onUpdated({ ...contact, ...changes });
      }
      setStatus('saved');
      setTimeout(() => { setEditing(false); setStatus(null); }, 900);
    } catch { setStatus('error'); } finally { setSaving(false); }
  }

  const avatarStyle = getAvatarStyle(contact.name);
  const initials    = getInitials(contact.name);

  const editBtn = contactId
    ? <button style={s.editSmallBtn} onClick={(e) => { e.stopPropagation(); startEdit(); }}>Edit</button>
    : null;

  const contactUrl = contactId ? mondayItemUrl(CONTACTS_BOARD_ID, contactId) : null;
  const mondayBtn = contactUrl
    ? <button title={contactUrl} style={{ ...s.mondayLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={(e) => { e.stopPropagation(); window.open(contactUrl, '_blank'); }}>↗ Contact Record</button>
    : null;

  if (editing) {
    return (
      <CollapsibleCard label="Contact Info">
        {['name', 'company', 'phone', 'email'].map((field) => (
          <div key={field} style={s.inputRow}>
            <label style={s.inputLabel}>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
            <input
              style={s.input}
              value={form[field] || ''}
              onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
              type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
            />
          </div>
        ))}
        {status === 'saved'  && <p style={{ ...s.saveStatus, color: C.teal }}>✓ Saved</p>}
        {status === 'error'  && <p style={{ ...s.saveStatus, color: C.red }}>Save failed — try again</p>}
        <div style={s.editActions}>
          <button style={s.saveEditBtn} onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button style={s.cancelEditBtn} onClick={cancelEdit}>Cancel</button>
        </div>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard label="Contact Info" right={<>{mondayBtn}{editBtn}</>}>
      {/* Header row: avatar + name */}
      <div style={{ ...s.contactHeader, marginBottom: '10px' }}>
        <div style={{ ...s.avatar, backgroundColor: avatarStyle.bg, color: avatarStyle.color }}>
          {initials}
        </div>
        <div style={{ flex: 1 }}>
          <p style={s.contactName}>{contact.name || '—'}</p>
          {contact.company && <p style={s.contactCompany}>{contact.company}</p>}
        </div>
      </div>

      {/* Contact info rows */}
      <div style={{ ...s.infoRow, borderTop: `1px solid ${C.borderSoft}` }}>
        <span style={s.infoLabel}>Phone</span>
        {contact.phone
          ? <a href={`tel:${contact.phone}`} style={s.infoLink}>{contact.phone}</a>
          : <span style={{ ...s.infoValue, color: C.textTertiary }}>Not on file</span>}
      </div>
      <div style={{ ...s.infoRow, borderBottom: 'none' }}>
        <span style={s.infoLabel}>Email</span>
        {contact.email
          ? <a href={`mailto:${contact.email}`} style={s.infoLink}>{contact.email}</a>
          : <span style={{ ...s.infoValue, color: C.textTertiary }}>Not on file</span>}
      </div>
    </CollapsibleCard>
  );
}

// ── Contact Notes Section ───────────────────────────────────────────────────
function ContactNotesSection({ contact, contactId, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(null);

  function startEdit() { setDraft(contact.notes || ''); setEditing(true); setStatus(null); }
  function cancelEdit() { setEditing(false); setDraft(''); setStatus(null); }

  async function saveEdit() {
    setSaving(true); setStatus(null);
    try {
      await updateContactDetails(contactId, { notes: draft });
      onUpdated({ ...contact, notes: draft });
      setStatus('saved');
      setTimeout(() => { setEditing(false); setStatus(null); }, 900);
    } catch { setStatus('error'); } finally { setSaving(false); }
  }

  const editBtn = contactId
    ? <button style={s.editSmallBtn} onClick={(e) => { e.stopPropagation(); startEdit(); }}>Edit</button>
    : null;

  return (
    <CollapsibleCard label="Contact Notes" right={!editing ? editBtn : null}>
      {editing ? (
        <>
          <textarea
            style={{ ...s.input, minHeight: '90px', resize: 'vertical', lineHeight: '1.5' }}
            value={draft}
            placeholder="General notes about this contact — not deal-specific…"
            onChange={(e) => setDraft(e.target.value)}
          />
          {status === 'saved' && <p style={{ ...s.saveStatus, color: C.teal }}>✓ Saved</p>}
          {status === 'error' && <p style={{ ...s.saveStatus, color: C.red }}>Save failed — try again</p>}
          <div style={s.editActions}>
            <button style={s.saveEditBtn} onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button style={s.cancelEditBtn} onClick={cancelEdit}>Cancel</button>
          </div>
        </>
      ) : contact.notes ? (
        <p style={{ ...s.noteText, fontStyle: 'normal', color: C.textSecondary }}>{contact.notes}</p>
      ) : (
        <p style={s.noHistory}>No notes yet.{contactId ? ' Tap Edit to add some.' : ''}</p>
      )}
    </CollapsibleCard>
  );
}

// ── Prospect Details Section ────────────────────────────────────────────────
function ProspectDetailsSection({ prospectItem, onUpdated, prospectId }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(null);

  const priority     = getCol(prospectItem, 'priority_1');
  const prospectType = getCol(prospectItem, 'status1');
  const followUp     = getCol(prospectItem, 'date');
  const lastContact  = getCol(prospectItem, 'date1');

  function startEdit() {
    setForm({ priority, prospectType }); setEditing(true); setStatus(null);
  }
  function cancelEdit() { setEditing(false); setForm({}); setStatus(null); }

  async function saveEdit() {
    setSaving(true); setStatus(null);
    try {
      const changes = {};
      if (form.priority !== priority) changes.priority = form.priority;
      if (form.prospectType !== prospectType) changes.prospectType = form.prospectType;
      if (Object.keys(changes).length > 0) {
        await updateProspectDetails(prospectItem.id, changes);
        onUpdated(changes);
      }
      setStatus('saved');
      setTimeout(() => { setEditing(false); setStatus(null); }, 900);
    } catch { setStatus('error'); } finally { setSaving(false); }
  }

  const pTag = priorityTag(priority);

  const editBtn = (
    <button style={s.editSmallBtn} onClick={(e) => { e.stopPropagation(); startEdit(); }}>Edit</button>
  );

  const prospectUrl = prospectId ? mondayItemUrl(PROSPECTING_BOARD_ID, prospectId) : null;
  const mondayBtn = prospectUrl
    ? <button title={prospectUrl} style={{ ...s.mondayLink, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={(e) => { e.stopPropagation(); window.open(prospectUrl, '_blank'); }}>↗ Prospecting Record</button>
    : null;

  if (editing) {
    return (
      <CollapsibleCard label="Prospect Details">
        <div style={s.inputRow}>
          <label style={s.inputLabel}>Priority</label>
          <select style={s.select} value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
            {PRIORITY_OPTIONS.map((o) => <option key={o} value={o}>{o.replace(/^\(\d\)\s*/, '')}</option>)}
          </select>
        </div>
        <div style={s.inputRow}>
          <label style={s.inputLabel}>Prospect Type</label>
          <select style={s.select} value={form.prospectType} onChange={(e) => setForm((f) => ({ ...f, prospectType: e.target.value }))}>
            {PROSPECT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        {status === 'saved' && <p style={{ ...s.saveStatus, color: C.teal }}>✓ Saved</p>}
        {status === 'error' && <p style={{ ...s.saveStatus, color: C.red }}>Save failed — try again</p>}
        <div style={s.editActions}>
          <button style={s.saveEditBtn} onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button style={s.cancelEditBtn} onClick={cancelEdit}>Cancel</button>
        </div>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard label="Prospect Details" right={<>{mondayBtn}{editBtn}</>}>
      <div style={s.detailGrid}>
        <div>
          <p style={s.detailLabel}>Priority</p>
          <span style={{ ...s.detailValue, display: 'inline-block', padding: '2px 9px', borderRadius: '6px', backgroundColor: pTag.bg, color: pTag.color }}>{priorityLabel(priority)}</span>
        </div>
        <div>
          <p style={s.detailLabel}>Type</p>
          <p style={s.detailValue}>{prospectType || '—'}</p>
        </div>
        <div>
          <p style={s.detailLabel}>Next Follow Up</p>
          <p style={s.detailValue}>{formatDate(followUp)}</p>
        </div>
        <div>
          <p style={s.detailLabel}>Last Contact</p>
          <p style={s.detailValue}>{formatDate(lastContact)}</p>
        </div>
      </div>
    </CollapsibleCard>
  );
}

// ── Requirement Card (single subitem display) ──────────────────────────────
function RequirementCard({ subitem, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const getT = (id) => subitem.column_values?.find((c) => c.id === id)?.text || '';

  const status       = getT('status_1');
  const assignType   = getT('dropdown');
  const assetTypes   = getT('dropdown2');
  const sfMin        = getT('numbers4');
  const sfMax        = getT('numbers5');
  const acresMin     = getT('numbers3');
  const acresMax     = getT('numbers59');
  const priceMin     = getT('numbers');
  const priceMax     = getT('numbers1');
  const location     = getT('dropdown6');
  const notes        = getT('text');

  const sc         = reqStatusColor(status);
  const sfRange    = rangeStr(sfMin, sfMax, 'SF');
  const acresRange = acresRangeStr(acresMin, acresMax);   // plain decimals, no K/M
  const priceRange = rangeStr(priceMin, priceMax, '', true);

  const summaryParts = [assetTypes, sfRange || acresRange, priceRange, location ? `📍 ${location}` : '']
    .filter(Boolean);

  return (
    <div style={{ padding: '11px 0', borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}` }}>
      {/* Header row */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: C.textPrimary }}>
            {subitem.name || 'Requirement'}
          </span>
          {assignType && (
            <span style={{ fontSize: '11px', color: C.textSecondary, marginLeft: '6px' }}>
              {assignType}
            </span>
          )}
        </div>
        <span style={{
          ...s.tag, ...sc, fontSize: '10px', fontWeight: '700',
          padding: '2px 8px', borderRadius: '5px', whiteSpace: 'nowrap',
        }}>
          {status || 'Looking'}
        </span>
        <span style={{
          ...s.chevron,
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        }}>▾</span>
      </div>

      {/* Summary chips */}
      {summaryParts.length > 0 && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '5px' }}>
          {summaryParts.map((p, i) => (
            <span key={i} style={{ fontSize: '11px', color: C.textSecondary }}>{p}</span>
          ))}
        </div>
      )}

      {/* Expanded detail grid */}
      {expanded && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px dashed ${C.borderSoft}` }}>
          <div style={s.detailGrid}>
            {sfRange && (
              <div>
                <p style={s.detailLabel}>Square Feet</p>
                <p style={s.detailValue}>{sfRange}</p>
              </div>
            )}
            {acresRange && (
              <div>
                <p style={s.detailLabel}>Acres</p>
                <p style={s.detailValue}>{acresRange}</p>
              </div>
            )}
            {priceRange && (
              <div>
                <p style={s.detailLabel}>Price Range</p>
                <p style={s.detailValue}>{priceRange}</p>
              </div>
            )}
            {location && (
              <div>
                <p style={s.detailLabel}>Location</p>
                <p style={s.detailValue}>{location}</p>
              </div>
            )}
          </div>
          {notes && (
            <p style={{ ...s.noteText, fontStyle: 'normal', marginTop: '8px', color: C.textSecondary }}>
              {notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Requirements Section ────────────────────────────────────────────────────
function RequirementsSection({ subitems, prospectItemId, onRequirementAdded }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm]     = useState({ ...DEFAULT_REQ_FORM });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  function cancelAdd() { setAdding(false); setForm({ ...DEFAULT_REQ_FORM }); setSaveError(null); }

  function toggleAssetType(type) {
    setForm((f) => ({
      ...f,
      assetTypes: f.assetTypes.includes(type)
        ? f.assetTypes.filter((t) => t !== type)
        : [...f.assetTypes, type],
    }));
  }

  async function handleSave() {
    setSaving(true); setSaveError(null);
    try {
      const name = autoReqName(form);
      const newItem = await createSubitem(prospectItemId, { ...form, name });
      onRequirementAdded(newItem);
      cancelAdd();
    } catch (err) {
      setSaveError(err.message || 'Failed to add requirement — try again.');
    } finally {
      setSaving(false);
    }
  }

  const count  = subitems.length;
  const addBtn = (
    <button
      style={s.editSmallBtn}
      onClick={(e) => { e.stopPropagation(); setAdding(true); }}
    >
      + Add
    </button>
  );

  return (
    <CollapsibleCard
      label={`Requirements${count > 0 ? ` (${count})` : ''}`}
      defaultOpen={true}
      right={!adding ? addBtn : null}
    >
      {/* Existing requirements */}
      {subitems.map((sub, i) => (
        <RequirementCard key={sub.id || i} subitem={sub} isLast={i === subitems.length - 1 && !adding} />
      ))}

      {subitems.length === 0 && !adding && (
        <p style={s.noHistory}>No requirements on file. Tap + Add to create one.</p>
      )}

      {/* ── Add form ── */}
      {adding && (
        <div style={{
          marginTop: subitems.length > 0 ? '14px' : '0',
          paddingTop: subitems.length > 0 ? '14px' : '0',
          borderTop: subitems.length > 0 ? `1px solid ${C.border}` : 'none',
        }}>
          <p style={{ ...s.detailLabel, marginBottom: '12px', color: C.indigo }}>New Requirement</p>

          {/* Status */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Status</label>
            <select
              style={s.select}
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {REQUIREMENT_STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Assignment Type */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Assignment Type</label>
            <select
              style={s.select}
              value={form.assignmentType}
              onChange={(e) => setForm((f) => ({ ...f, assignmentType: e.target.value }))}
            >
              <option value="">— Select —</option>
              {ASSIGNMENT_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Asset Types — pill toggles */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ ...s.inputLabel, display: 'block', marginBottom: '7px' }}>Asset Types</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {ASSET_TYPES_LIST.map((type) => {
                const on = form.assetTypes.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleAssetType(type)}
                    style={{
                      padding: '5px 12px', borderRadius: '8px', fontSize: '12px',
                      fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                      border: `1.5px solid ${on ? C.indigo : C.border}`,
                      backgroundColor: on ? C.indigoLight : C.bgCard,
                      color: on ? C.indigo : C.textSecondary,
                      transition: 'all 0.1s',
                    }}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SF Range */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Square Feet</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Min SF"
                type="number"
                min="0"
                value={form.sfMin}
                onChange={(e) => setForm((f) => ({ ...f, sfMin: e.target.value }))}
              />
              <span style={{ color: C.textTertiary, fontSize: '13px' }}>–</span>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Max SF"
                type="number"
                min="0"
                value={form.sfMax}
                onChange={(e) => setForm((f) => ({ ...f, sfMax: e.target.value }))}
              />
            </div>
          </div>

          {/* Acres Range */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Acres</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Min"
                type="number"
                min="0"
                step="0.01"
                value={form.acresMin}
                onChange={(e) => setForm((f) => ({ ...f, acresMin: e.target.value }))}
              />
              <span style={{ color: C.textTertiary, fontSize: '13px' }}>–</span>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Max"
                type="number"
                min="0"
                step="0.01"
                value={form.acresMax}
                onChange={(e) => setForm((f) => ({ ...f, acresMax: e.target.value }))}
              />
            </div>
          </div>

          {/* Price Range */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Price Range ($)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Min $"
                type="number"
                min="0"
                value={form.priceMin}
                onChange={(e) => setForm((f) => ({ ...f, priceMin: e.target.value }))}
              />
              <span style={{ color: C.textTertiary, fontSize: '13px' }}>–</span>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="Max $"
                type="number"
                min="0"
                value={form.priceMax}
                onChange={(e) => setForm((f) => ({ ...f, priceMax: e.target.value }))}
              />
            </div>
          </div>

          {/* Location */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Location</label>
            <select
              style={s.select}
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            >
              <option value="">— Any —</option>
              {LOCATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div style={s.inputRow}>
            <label style={s.inputLabel}>Notes</label>
            <textarea
              style={{ ...s.input, minHeight: '70px', resize: 'vertical', lineHeight: '1.5' }}
              placeholder="Specific requirements, preferences, deal notes…"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>

          {saveError && (
            <p style={{ ...s.saveStatus, color: C.red, marginBottom: '8px' }}>{saveError}</p>
          )}

          <div style={s.editActions}>
            <button style={s.saveEditBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Adding…' : 'Add Requirement'}
            </button>
            <button style={s.cancelEditBtn} onClick={cancelAdd}>Cancel</button>
          </div>
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── Discard Screen ──────────────────────────────────────────────────────────
function DiscardScreen({ prospectItem, contactId, onConfirmed, onCancel }) {
  const [reason, setReason]       = useState('');
  const [discarding, setDiscarding] = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState(null);

  async function handleConfirm() {
    if (!reason) return;
    setDiscarding(true); setError(null);
    try {
      await discardProspect(prospectItem.id, contactId, reason);
      setDone(true);
      setTimeout(() => onConfirmed(), 900);
    } catch (err) {
      setError(err.message || 'Discard failed — try again.');
      setDiscarding(false);
    }
  }

  if (discarding) {
    return (
      <div style={s.page}>
        <div style={{ ...s.loading, paddingTop: '80px' }}>
          {done ? '✅ Discarded. Returning to queue…' : 'Saving…'}
        </div>
      </div>
    );
  }

  const isDNC = reason === 'Hostile—DNC';

  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <p style={{ ...s.navTitle, color: C.red }}>Discard</p>
        <span style={s.navSub}>{prospectItem.name.split('|')[0].trim()}</span>
      </div>
      <div style={s.body}>
        {error && <div style={s.error}><strong>Error:</strong> {error}</div>}
        <div style={s.card}>
          <p style={s.cardLabel}>Why are you discarding this prospect?</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            {DISCARD_REASONS.map((r) => {
              const selected = reason === r;
              const isDncOpt = DISCARD_DNC.includes(r);
              return (
                <button key={r} onClick={() => setReason(r)} style={{
                  textAlign: 'left', padding: '12px 14px', borderRadius: '10px',
                  border: selected ? `2px solid ${isDncOpt ? C.red : C.indigo}` : `1.5px solid ${C.border}`,
                  backgroundColor: selected ? (isDncOpt ? C.redLight : C.indigoLight) : C.bgCard,
                  color: selected ? (isDncOpt ? C.red : C.indigo) : C.textPrimary,
                  fontSize: '14px', fontWeight: selected ? '700' : '500',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  {r}
                  {isDncOpt && <span style={{ fontSize: '11px', marginLeft: '8px', fontWeight: '400', opacity: 0.7 }}>— flags Do Not Contact</span>}
                </button>
              );
            })}
          </div>
        </div>

        {reason && (
          <div style={isDNC ? { ...s.warning, background: C.redLight, borderColor: C.redBorder, color: '#991B1B' } : s.warning}>
            {isDNC
              ? '⛔ This will mark the contact as Do Not Contact and set status to Lost.'
              : 'This will set the prospect\'s status to Lost and remove them from your queue.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={handleConfirm}
            disabled={!reason}
            style={{
              ...s.btnDanger,
              backgroundColor: reason ? C.red : C.bgSoft,
              color: reason ? '#fff' : C.textTertiary,
              border: 'none', fontWeight: '700', fontSize: '15px',
              cursor: reason ? 'pointer' : 'not-allowed',
            }}
          >
            Confirm Discard
          </button>
          <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Reassign Screen ─────────────────────────────────────────────────────────
function ReassignScreen({ prospectItem, brokerName, onConfirmed, onCancel }) {
  const [selected, setSelected]   = useState('');
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState(null);

  // All brokers except the one currently assigned
  const teammates = BROKERS.filter((b) => b.name !== brokerName);

  async function handleConfirm() {
    if (!selected) return;
    setSaving(true); setError(null);
    try {
      await reassignProspect(prospectItem.id, selected, brokerName);
      setDone(true);
      setTimeout(() => onConfirmed(), 900);
    } catch (err) {
      setError(err.message || 'Reassign failed — try again.');
      setSaving(false);
    }
  }

  if (saving) {
    return (
      <div style={s.page}>
        <div style={{ ...s.loading, paddingTop: '80px' }}>
          {done ? `✅ Sent to ${selected}. Returning to queue…` : 'Saving…'}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <p style={{ ...s.navTitle, color: C.indigo }}>Send to Teammate</p>
        <span style={s.navSub}>{prospectItem.name.split('|')[0].trim()}</span>
      </div>
      <div style={s.body}>
        {error && <div style={s.error}><strong>Error:</strong> {error}</div>}
        <div style={s.card}>
          <p style={s.cardLabel}>Who should take this one?</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            {teammates.map((b) => {
              const sel = selected === b.name;
              const av  = getAvatarStyle(b.name);
              return (
                <button
                  key={b.name}
                  onClick={() => setSelected(b.name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    textAlign: 'left', padding: '12px 14px', borderRadius: '10px',
                    border: sel ? `2px solid ${C.indigo}` : `1.5px solid ${C.border}`,
                    backgroundColor: sel ? C.indigoLight : C.bgCard,
                    color: sel ? C.indigo : C.textPrimary,
                    fontSize: '14px', fontWeight: sel ? '700' : '500',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700',
                    backgroundColor: av.bg, color: av.color,
                  }}>
                    {b.initials}
                  </div>
                  {b.name}
                </button>
              );
            })}
          </div>
        </div>

        {selected && (
          <div style={s.warning}>
            This will reassign the record to <strong>{selected}</strong> and log the handoff on the Monday record.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={handleConfirm}
            disabled={!selected}
            style={{
              ...s.btnPrimary,
              backgroundColor: selected ? C.indigo : C.bgSoft,
              color: selected ? '#fff' : C.textTertiary,
              cursor: selected ? 'pointer' : 'not-allowed',
              fontWeight: '700', fontSize: '15px',
            }}
          >
            Send to {selected || 'Teammate'}
          </button>
          <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function ContactCard({ prospectItem: initialProspectItem, brokerName, onBack, onLogActivity, onSkip, onEmail }) {
  const [prospectItem, setProspectItem] = useState(initialProspectItem);
  const [contact, setContact]         = useState(null);
  const [history, setHistory]         = useState([]);
  const [updates, setUpdates]         = useState([]);
  const [subitems, setSubitems]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [showReassign, setShowReassign] = useState(false);

  const contactId = getLinkedContactId(prospectItem);

  useEffect(() => {
    if (!contactId) {
      // No linked Contact record — still fetch subitems from the Prospecting record
      setContact({ name: prospectItem.name.split('|')[0].trim(), phone: '', email: '', company: '', notes: '', unlinked: true });
      fetchSubitems(prospectItem.id)
        .then(setSubitems)
        .catch(() => {}) // subitems non-critical — don't block the page
        .finally(() => setLoading(false));
      return;
    }
    Promise.all([
      fetchContactDetails(contactId),
      fetchCallHistory(contactId),
      fetchContactUpdates(contactId),
      fetchSubitems(prospectItem.id),
    ])
      .then(([contactData, historyData, updatesData, subitemsData]) => {
        setContact(contactData);
        setHistory(historyData);
        setUpdates(updatesData);
        setSubitems(subitemsData);
        setLoading(false);
      })
      .catch((err) => { setError(err.message || 'Failed to load contact details.'); setLoading(false); });
  }, []); // eslint-disable-line

  function handleProspectUpdated(changes) {
    setProspectItem((prev) => {
      const newCols = prev.column_values.map((col) => {
        if (col.id === 'priority_1' && changes.priority)    return { ...col, text: changes.priority };
        if (col.id === 'status1'    && changes.prospectType) return { ...col, text: changes.prospectType };
        return col;
      });
      return { ...prev, column_values: newCols };
    });
  }

  if (showDiscard) {
    return (
      <DiscardScreen
        prospectItem={prospectItem}
        contactId={contactId}
        onConfirmed={onBack}
        onCancel={() => setShowDiscard(false)}
      />
    );
  }

  if (showReassign) {
    return (
      <ReassignScreen
        prospectItem={prospectItem}
        brokerName={brokerName}
        onConfirmed={onBack}
        onCancel={() => setShowReassign(false)}
      />
    );
  }

  // Last Note: manual activity entries only (calls, meetings, etc.) — emails excluded
  const lastNote = history.find((h) => h.notes && h.activityType !== 'Email')?.notes;

  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onBack}>←</button>
        <p style={s.navTitle}>{prospectItem.name.split('|')[0].trim()}</p>
        <span style={s.navSub}>Queue</span>
      </div>

      <div style={s.body}>
        {loading && <div style={s.loading}>Loading contact…</div>}
        {error   && <div style={s.error}><strong>Error:</strong> {error}</div>}

        {!loading && !error && contact && (
          <>
            {contact.unlinked && (
              <div style={s.warning}>
                ⚠️ No linked Contact record in Monday. Phone and email unavailable.
              </div>
            )}

            {/* Contact info card */}
            <ContactInfoSection
              contact={contact}
              contactId={contactId}
              onUpdated={setContact}
            />

            {/* Contact notes */}
            <ContactNotesSection
              contact={contact}
              contactId={contactId}
              onUpdated={setContact}
            />

            {/* Prospect details */}
            <ProspectDetailsSection
              prospectItem={prospectItem}
              prospectId={prospectItem.id}
              onUpdated={handleProspectUpdated}
            />

            {/* Requirements (subitems on Prospecting record) */}
            <RequirementsSection
              subitems={subitems}
              prospectItemId={prospectItem.id}
              onRequirementAdded={(newItem) => {
                if (newItem) setSubitems((prev) => [...prev, newItem]);
              }}
            />

            {/* Last note — surfaced from history */}
            {lastNote && (
              <CollapsibleCard label="Last Note">
                <p style={s.noteText}>"{lastNote.length > 200 ? lastNote.slice(0, 200) + '…' : lastNote}"</p>
              </CollapsibleCard>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...s.btnPrimary,    flex: 2, padding: '12px 8px', fontSize: '14px' }} onClick={onLogActivity}>Log Activity</button>
              <button style={{ ...s.btnSecondary,  flex: 1, padding: '12px 8px', fontSize: '13px' }} onClick={() => onSkip(prospectItem.id)}>Skip</button>
              <button style={{ ...s.btnDanger,     flex: 1, padding: '12px 8px', fontSize: '13px' }} onClick={() => setShowDiscard(true)}>Discard</button>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {onEmail && contact?.email && (
                <button
                  style={{ ...s.btnSecondary, flex: 1, fontSize: '13px', color: C.teal, borderColor: C.tealLight }}
                  onClick={() => onEmail({
                    email:            contact.email,
                    name:             contact.name,
                    company:          contact.company,
                    title:            contact.title,
                    notes:            contact.notes,
                    lastActivity:     history[0]?.activityType,
                    lastActivityDate: history[0]?.date,
                    prospectType:     getCol(prospectItem, 'status1'),
                    priority:         getCol(prospectItem, 'priority_1'),
                    contactItemId:    contactId,
                    // Last 3 logged activities for richer AI context
                    recentActivities: history.slice(0, 3).map((h) => ({
                      type:    h.activityType,
                      outcome: h.outcome,
                      date:    h.date,
                      notes:   h.notes,
                    })),
                  })}
                >
                  ✉ Email
                </button>
              )}
              <button
                style={{ ...s.btnSecondary, flex: 1, fontSize: '13px', color: C.indigo, borderColor: C.indigoLight }}
                onClick={() => setShowReassign(true)}
              >
                ↩ Send to Teammate
              </button>
            </div>

            {/* Call history + Monday updates merged */}
            {(() => {
              const merged = [
                ...history.map((e) => ({ ...e, source: 'log' })),
                ...updates,
              ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
              const total = merged.length;
              return (
                <CollapsibleCard label={`Activity & Notes${total > 0 ? ` (${total})` : ''}`} defaultOpen={true}>
                  {total === 0 ? (
                    <p style={s.noHistory}>No activity logged yet.</p>
                  ) : (
                    merged.map((entry, i) => (
                      <div key={i} style={{ ...s.historyEntry, ...(i === total - 1 ? { borderBottom: 'none' } : {}) }}>
                        <div style={s.historyMeta}>
                          <span style={s.historyDate}>{formatDate(entry.date)}</span>
                          {entry.source === 'update' ? (
                            <>
                              <span style={s.updateBadge}>Monday Update</span>
                              {entry.author && <span style={s.historyType}>{entry.author}</span>}
                            </>
                          ) : (
                            <>
                              <span style={s.historyType}>{entry.activityType || 'Activity'}</span>
                              {entry.outcome && (
                                <>
                                  <span style={{ ...s.outcomeDot, backgroundColor: outcomeColor(entry.outcome) }} />
                                  <span style={{ ...s.historyOutcome, color: outcomeColor(entry.outcome) }}>{entry.outcome}</span>
                                </>
                              )}
                              {entry.broker && (
                                <span style={{ fontSize: '11px', color: C.textTertiary, marginLeft: 'auto' }}>
                                  {entry.broker.split(' ')[0]}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {(entry.notes || entry.text) && (
                          <p style={s.historyNotes}>
                            {(() => { const t = entry.notes || entry.text || ''; return t.length > 180 ? t.slice(0, 180) + '…' : t; })()}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </CollapsibleCard>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
